<script setup lang="ts">
import MemberHeader from "@/components/member/MemberHeader.vue";
import DeveloperSidebar from "@/components/developer/DeveloperSidebar.vue";
import { useRouter } from "vue-router";

const router = useRouter();

const creditBatches = [
  {
    issuanceRef: 'CCI-2024-00089-001',
    project: 'Solar Park Beta – Northern Highlands',
    projectId: 'P-2024-0031',
    monitoringPeriod: 'Apr – Sep 2024',
    issuedDate: '2024-12-01',
    totalIssued: 6120,
    available: 5840,
    retired: 280,
    transferred: 0,
    vintage: '2024',
    status: 'Active',
  },
  {
    issuanceRef: 'CCI-2022-00014-003',
    project: 'Wind Farm Alpha – Coastal Region',
    projectId: 'P-2022-0014',
    monitoringPeriod: 'Jan – Dec 2023',
    issuedDate: '2024-02-10',
    totalIssued: 15200,
    available: 0,
    retired: 15200,
    transferred: 0,
    vintage: '2023',
    status: 'Fully Retired',
  },
]

const transactions = [
  { date: '2025-02-14', type: 'Retirement', project: 'Solar Park Beta', amount: 200, ref: 'RET-2025-0041', status: 'Complete' },
  { date: '2025-01-05', type: 'Retirement', project: 'Solar Park Beta', amount: 80, ref: 'RET-2025-0008', status: 'Complete' },
  { date: '2024-12-01', type: 'Issuance', project: 'Solar Park Beta', amount: 6120, ref: 'CCI-2024-00089-001', status: 'Complete' },
  { date: '2024-02-10', type: 'Issuance', project: 'Wind Farm Alpha', amount: 15200, ref: 'CCI-2022-00014-003', status: 'Complete' },
]

const txTypeClass = (type: string) => {
  const map: Record<string, string> = {
    'Issuance': 'bg-green-100 text-green-800',
    'Retirement': 'bg-red-100 text-red-800',
    'Transfer': 'bg-blue-100 text-blue-800',
  }
  return map[type] ?? 'bg-gray-100 text-gray-600'
}
</script>

<template>
  <MemberHeader />

  <div class="flex h-screen bg-gray-50">
    <DeveloperSidebar />

    <main class="flex-1 overflow-auto py-8">
      <div class="max-w-6xl mx-auto px-5">

        <!-- Header -->
        <section class="mb-8">
          <div class="bg-white rounded-lg border border-gray-200 p-6">
            <div class="flex items-start justify-between">
              <div>
                <h1 class="text-3xl font-bold text-accent mb-2">Credit Portfolio</h1>
                <p class="text-gray-600">View and manage all your issued carbon credits across projects.</p>
              </div>
              <div class="flex items-center space-x-2">
                <button @click="router.push('/developer/retire-credits')" class="px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm hover:bg-gray-50">
                  <i class="fas fa-fire mr-2"></i>Retire Credits
                </button>
                <button @click="router.push('/developer/transfer-credits')" class="px-4 py-2 bg-primary text-white rounded-md text-sm hover:bg-primary/90">
                  <i class="fas fa-exchange-alt mr-2"></i>Transfer Credits
                </button>
              </div>
            </div>
          </div>
        </section>

        <!-- Portfolio summary -->
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div class="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <p class="text-2xl font-bold text-primary">21,320</p>
            <p class="text-sm text-gray-600">Total Issued (tCO2e)</p>
          </div>
          <div class="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <p class="text-2xl font-bold text-green-600">5,840</p>
            <p class="text-sm text-gray-600">Available</p>
          </div>
          <div class="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <p class="text-2xl font-bold text-gray-500">15,480</p>
            <p class="text-sm text-gray-600">Retired / Cancelled</p>
          </div>
          <div class="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <p class="text-2xl font-bold text-blue-600">0</p>
            <p class="text-sm text-gray-600">In Transfer</p>
          </div>
        </div>

        <!-- Credit batches -->
        <section class="mb-8">
          <h2 class="text-lg font-semibold text-accent mb-4">Issuance Batches</h2>
          <div class="space-y-4">
            <div
              v-for="batch in creditBatches"
              :key="batch.issuanceRef"
              class="bg-white rounded-lg border border-gray-200 p-5"
            >
              <div class="flex items-start justify-between mb-4">
                <div>
                  <div class="flex items-center space-x-3 mb-1">
                    <h3 class="font-medium text-accent">{{ batch.project }}</h3>
                    <span :class="[
                      'inline-flex px-2 py-0.5 rounded-full text-xs font-medium',
                      batch.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                    ]">{{ batch.status }}</span>
                  </div>
                  <p class="text-sm text-gray-500">Ref: {{ batch.issuanceRef }} &nbsp;·&nbsp; Monitoring: {{ batch.monitoringPeriod }} &nbsp;·&nbsp; Vintage {{ batch.vintage }}</p>
                </div>
                <div class="text-right text-sm text-gray-500">
                  <p>Issued {{ batch.issuedDate }}</p>
                </div>
              </div>
              <div class="grid grid-cols-3 gap-4 text-sm text-center mb-4">
                <div class="bg-primary/5 rounded-md p-3">
                  <p class="text-lg font-bold text-primary">{{ batch.totalIssued.toLocaleString() }}</p>
                  <p class="text-xs text-gray-600">Total Issued</p>
                </div>
                <div class="bg-green-50 rounded-md p-3">
                  <p class="text-lg font-bold text-green-600">{{ batch.available.toLocaleString() }}</p>
                  <p class="text-xs text-gray-600">Available</p>
                </div>
                <div class="bg-gray-50 rounded-md p-3">
                  <p class="text-lg font-bold text-gray-500">{{ batch.retired.toLocaleString() }}</p>
                  <p class="text-xs text-gray-600">Retired</p>
                </div>
              </div>
              <div v-if="batch.available > 0" class="flex items-center space-x-3">
                <button @click="router.push('/developer/retire-credits')" class="px-4 py-1.5 border border-gray-300 text-gray-700 rounded-md text-sm hover:bg-gray-50">
                  <i class="fas fa-fire mr-1"></i>Retire
                </button>
                <button @click="router.push('/developer/transfer-credits')" class="px-4 py-1.5 border border-primary text-primary rounded-md text-sm hover:bg-primary/5">
                  <i class="fas fa-exchange-alt mr-1"></i>Transfer
                </button>
                <button class="px-4 py-1.5 border border-gray-300 text-gray-700 rounded-md text-sm hover:bg-gray-50">
                  <i class="fas fa-download mr-1"></i>Download Certificate
                </button>
              </div>
            </div>
          </div>
        </section>

        <!-- Transaction history -->
        <section>
          <h2 class="text-lg font-semibold text-accent mb-4">Transaction History</h2>
          <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table class="w-full text-sm">
              <thead class="bg-gray-50 text-gray-700 text-xs uppercase tracking-wide">
                <tr>
                  <th class="px-5 py-3 text-left">Date</th>
                  <th class="px-5 py-3 text-left">Type</th>
                  <th class="px-5 py-3 text-left">Project</th>
                  <th class="px-5 py-3 text-right">Amount (tCO2e)</th>
                  <th class="px-5 py-3 text-left">Reference</th>
                  <th class="px-5 py-3 text-left">Status</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr v-for="tx in transactions" :key="tx.ref" class="hover:bg-gray-50">
                  <td class="px-5 py-3 text-gray-700">{{ tx.date }}</td>
                  <td class="px-5 py-3">
                    <span :class="['inline-flex px-2 py-0.5 rounded-full text-xs font-medium', txTypeClass(tx.type)]">{{ tx.type }}</span>
                  </td>
                  <td class="px-5 py-3 text-gray-700">{{ tx.project }}</td>
                  <td class="px-5 py-3 text-right font-medium" :class="tx.type === 'Issuance' ? 'text-green-600' : 'text-gray-700'">
                    {{ tx.type === 'Issuance' ? '+' : '-' }}{{ tx.amount.toLocaleString() }}
                  </td>
                  <td class="px-5 py-3 text-gray-500 text-xs">{{ tx.ref }}</td>
                  <td class="px-5 py-3">
                    <span class="bg-green-100 text-green-800 inline-flex px-2 py-0.5 rounded-full text-xs font-medium">{{ tx.status }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

      </div>
    </main>
  </div>
</template>
